"use client";

import React, { FC, memo, useMemo } from "react";

import { cn } from "utils";
import { I18nUtil } from "@utils/i18n";
import { RatingScoreObjectsRight } from "@/src/components/RatingScore/RatingScoreObjectsRight";
import Translations from "@translate/components/CoPilot/360Reports/ReportItem/ReportScoreBadge";

const I18n = I18nUtil.createScoped(Translations);

const scoreStyleTextMap = {
  A: "bg-gradient-to-rb from-[#FFD352] via-[#FFFED6] to-[#FFD352]",
  B: "bg-gradient-to-rb from-[#555555] via-[#DBDBDB] to-[#555555]",
  C: "bg-gradient-to-rb from-[#FFA85F] via-[#FFE5CF] to-[#FFA85F]",
  D: "text-content_secondary",
} as const;

type ScoreLevel = keyof typeof scoreStyleTextMap;

const normalizeRating = (rating: string): ScoreLevel => {
  const match = rating.match(/^([A-D])/);
  return (match ? match[1] : "A") as ScoreLevel;
};

interface ReportScoreBadgeProps {
  /** 评级文本，例如 A+、B- */
  rate: string;
  /** 自定义类名 */
  className?: string;

  orientationClassName?: string;

  ratingClassName?: string;
}

export const ReportScoreBadge: FC<ReportScoreBadgeProps> = memo(
  ({ rate, className, orientationClassName, ratingClassName }) => {
    const scoreLevel = useMemo(() => normalizeRating(rate), [rate]);

    const textClassName = useMemo(
      () => scoreStyleTextMap[scoreLevel] ?? scoreStyleTextMap.A,
      [scoreLevel],
    );

    return (
      <div
        className={cn(
          "flex items-center min-w-[124px] justify-between",
          className,
        )}
      >
        <RatingScoreObjectsRight
          variant={scoreLevel}
          className={cn(
            "!w-[11px] !h-8 md:!w-5 md:!h-auto shrink-0",
            ratingClassName,
          )}
        />
        <div
          className={cn(
            "flex gap-1 md:gap-0 md:flex-col items-center",
            orientationClassName,
          )}
        >
          <div
            className={cn(
              "headline_regular_s md:headline_regular_m text-content_brand_primary bg-clip-text text-transparent",
              textClassName,
            )}
          >
            {rate}
          </div>
          <div className="body_medium_s text-content_tertiary whitespace-nowrap">
            {I18n.t("Final Score")}
          </div>
        </div>
        <RatingScoreObjectsRight
          variant={scoreLevel}
          className={cn(
            "!w-[11px] !h-8 md:!w-5 md:!h-auto rotate-y-[180deg] shrink-0",
            ratingClassName,
          )}
        />
      </div>
    );
  },
);

ReportScoreBadge.displayName = "ReportScoreBadge";
